import { Badge } from "@loot-os/ui/components/badge";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useTRPC } from "../lib/trpc";
import { EventsUrlField } from "./EventsUrlField";

/**
 * One environment's two event URLs, order events and box events, each saved on its own.
 * Platform staff only; the tRPC procedures refuse anyone else and an archived tenant.
 */
export function EnvironmentEventsUrls({
  tenantId,
  environment,
  archived,
}: {
  tenantId: string;
  environment: {
    id: string;
    name: string;
    type: string;
    orderEventsUrl: string | null;
    boxEventsUrl: string | null;
  };
  archived: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const refreshEnvironments = () =>
    queryClient.invalidateQueries({ queryKey: trpc.environments.list.queryOptions().queryKey });
  const saveOrder = useMutation(
    trpc.platform.tenants.setOrderEventsUrl.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          result.orderEventsUrl
            ? `Order events URL saved for ${environment.name}.`
            : `Order events off for ${environment.name}.`,
        );
        void refreshEnvironments();
      },
    }),
  );
  const saveBox = useMutation(
    trpc.platform.tenants.setBoxEventsUrl.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          result.boxEventsUrl
            ? `Box events URL saved for ${environment.name}.`
            : `Box events off for ${environment.name}.`,
        );
        void refreshEnvironments();
      },
    }),
  );

  return (
    <div className="grid gap-2 rounded-md border px-3 py-2 text-sm">
      <div className="inline-flex items-center gap-2 font-medium">
        {environment.name}
        <Badge variant="outline">{environment.type}</Badge>
      </div>
      <EventsUrlField
        inputId={`order-events-url-${environment.id}`}
        label="Order events URL"
        url={environment.orderEventsUrl}
        disabled={archived}
        pending={saveOrder.isPending}
        onSave={(orderEventsUrl, onSaved) =>
          saveOrder.mutate(
            { tenantId, environmentId: environment.id, orderEventsUrl },
            { onSuccess: onSaved },
          )
        }
      />
      <EventsUrlField
        inputId={`box-events-url-${environment.id}`}
        label="Box events URL"
        url={environment.boxEventsUrl}
        disabled={archived}
        pending={saveBox.isPending}
        onSave={(boxEventsUrl, onSaved) =>
          saveBox.mutate(
            { tenantId, environmentId: environment.id, boxEventsUrl },
            { onSuccess: onSaved },
          )
        }
      />
    </div>
  );
}
